/**
 * ``useResolvedSystemStatus`` — the shared read model over the live System Status
 * feed (ADR-024 §4/§5 amended 2026-07-24; Flow L §6).
 *
 * Composes the two shared query-cache sources into the kind-grouped, live-merged
 * component model the **Entity Details** panel resolves a ``status-component`` /
 * ``status-connection`` selection against:
 *   - the REST snapshot (:func:`useSystemStatusSnapshot`, shared query key — the
 *     System Status panel subscribes to the same key, so TanStack Query dedups
 *     the request to one);
 *   - the last SSE frame (:func:`useSystemStatusLiveFrame`, the single-writer
 *     cache key the System Status panel's stream publishes — this reader never
 *     opens a second :class:`EventSource`, so REQ-UI-035 "BFF-only, one stream"
 *     holds).
 *
 * The merge + grouping are the same pure functions the topology uses
 * (:func:`mergeStreamFrame` / :func:`groupByKind`), so a node resolved here and
 * the same node in the diagram agree (the consistency :func:`resolveNodeRender`
 * then guarantees for the stoplight). When no live frame is cached (pre-first-
 * frame, or the System Status panel — and thus its stream — is closed) the model
 * falls back to the REST snapshot unchanged (honest degradation, CLAUDE.md §8.1).
 *
 * Read-gated identically to the snapshot hook: a session with no reader role
 * issues no request and resolves an empty model.
 *
 * Cross-references:
 *   - ADR-024 §4/§5 (amended — status detail in Entity Details); Flow L §6.
 *   - REQ-STATUS-020/022 (snapshot + SSE); REQ-UI-026 (server state in TanStack
 *     Query); REQ-UI-035 (BFF-only); REQ-UI-051 (single-focus selection bus).
 */
import { useMemo } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { ApiError } from "@/lib/api-client";
import {
  groupByKind,
  mergeStreamFrame,
  type ComponentKind,
  type ResolvedComponent,
  type SystemStatusSnapshot,
} from "@/system-status/status-model";
import { useSystemStatusSnapshot } from "@/system-status/use-system-status-snapshot";
import { useSystemStatusLiveFrame } from "@/system-status/use-system-status-stream";

/** The shared, live-merged read model over the System Status feed. */
export interface ResolvedSystemStatusModel {
  /** The underlying snapshot query (loading / error / refetch surface). */
  query: UseQueryResult<SystemStatusSnapshot, ApiError>;
  /** The current snapshot, or ``undefined`` before it resolves. */
  snapshot: SystemStatusSnapshot | undefined;
  /** Snapshot components with the live SSE rollup overlaid (``[]`` pre-load). */
  merged: readonly ResolvedComponent[];
  /** The merged components grouped by (parsed) component kind. */
  byKind: ReadonlyMap<ComponentKind, ResolvedComponent[]>;
  /** Wall-clock ms of the last live frame, else the snapshot's ``generated_at``. */
  lastEventTime: number | null;
}

/** Resolve the shared live System Status model (see the module docstring). */
export function useResolvedSystemStatus(): ResolvedSystemStatusModel {
  const query = useSystemStatusSnapshot();
  const live = useSystemStatusLiveFrame();
  const snapshot = query.data;

  const merged = useMemo<readonly ResolvedComponent[]>(
    () => (snapshot ? mergeStreamFrame(snapshot, live?.frame ?? null) : []),
    [snapshot, live],
  );
  const byKind = useMemo(() => groupByKind(merged), [merged]);

  const lastEventTime = useMemo<number | null>(() => {
    if (live !== null) return live.receivedAt;
    if (snapshot === undefined) return null;
    const parsed = Date.parse(snapshot.generated_at);
    return Number.isNaN(parsed) ? null : parsed;
  }, [live, snapshot]);

  return { query, snapshot, merged, byKind, lastEventTime };
}
